import React from 'react';
import { Link } from 'react-router-dom';
import { Users, Target, Flag, Award, Briefcase } from 'lucide-react';

const About = () => {
  // Color palette from your existing app
  const colorPalette = {
    primary: '#922c40',   // Burgundy/wine red
    secondary: '#DC9750', // Bronze/gold
    tertiary: '#F3EAC0',  // Cream/beige
    light: '#F9F5E3',     // Lighter cream for backgrounds
    dark: '#1E2640',      // Navy/dark blue
    white: '#FFFFFF',
    black: '#333333'
  };

  return (
    <div style={{ backgroundColor: colorPalette.light }}>
      {/* Hero Section */}
      <section style={{
        backgroundColor: colorPalette.dark,
        color: 'white',
        padding: '80px 0 60px',
        textAlign: 'center'
      }}>
        <div className="container">
          <h1 style={{ 
            fontSize: '3rem',
            marginBottom: '16px',
            fontWeight: '800'
          }}>About Voters Deserve More</h1>
          <p style={{
            fontSize: '1.25rem',
            maxWidth: '800px',
            margin: '0 auto 24px',
            lineHeight: '1.6',
            opacity: '0.9'
          }}>
            A grassroots movement working to transform our nation into a vibrant, multi-party democracy.
          </p>
        </div>
      </section>

      {/* Mission and Vision Section */}
      <section style={{
        padding: '60px 0',
        backgroundColor: 'white'
      }}>
        <div className="container">
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: window.innerWidth < 768 ? '1fr' : '1fr 1fr',
            gap: '40px',
            alignItems: 'center'
          }}>
            <div>
              <h2 style={{
                color: colorPalette.primary,
                fontSize: '2.2rem',
                marginBottom: '20px'
              }}>Our Mission</h2>
              <p style={{ fontSize: '1.1rem', lineHeight: '1.6', marginBottom: '20px' }}>
              Voters Deserve More is dedicated to transforming America’s broken two-party system into a vibrant multi-party democracy, 
              free from the corruption, gridlock, and authoritarianism strangling our country. 
              </p>
              <p style={{ fontSize: '1.1rem', lineHeight: '1.6', marginBottom: '20px' }}>
              We believe in building coalitions across the political spectrum, working to mobilize the millions of Americans abandoned, 
              betrayed, and exploited under the two-party system into a mass movement capable of winning the democracy we deserve.
              </p>
              <div style={{
                padding: '16px',
                borderLeft: `4px solid ${colorPalette.primary}`,
                backgroundColor: colorPalette.light,
                marginTop: '30px'
              }}>
                <p style={{ fontSize: '1.1rem', fontWeight: '600', margin: 0 }}>
                  We believe in the power of proportional representation, public campaign financing, and direct democracy to win back power for we, the people.
                </p>
              </div>
            </div>
            
            <div style={{
              backgroundColor: colorPalette.tertiary,
              borderRadius: '8px',
              padding: '40px',
              boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{
                fontSize: '1.8rem',
                color: colorPalette.dark,
                marginBottom: '20px',
                textAlign: 'center'
              }}>Our Vision</h3>
              <p style={{ fontSize: '1.1rem', lineHeight: '1.6', marginBottom: '20px' }}>
              By forging a mass movement across the political spectrum, we can advance our demands for a New American Democracy at the polls and in the streets; 
              in the courts and in the halls of government. Together, we can end the two-party system and give Americans a real voice in governments of, by, and 
              for the people. 
              </p>
              <p style={{ fontSize: '1.1rem', lineHeight: '1.6' }}>
                We envision an America where:
              </p>
              <ul style={{ paddingLeft: '20px' }}>
                <li style={{ marginBottom: '10px', fontSize: '1.1rem' }}>Every vote really counts</li>
                <li style={{ marginBottom: '10px', fontSize: '1.1rem' }}>Multiple parties represent diverse perspectives</li>
                <li style={{ marginBottom: '10px', fontSize: '1.1rem' }}>Corporate and foreign money is out of politics</li>
                <li style={{ marginBottom: '10px', fontSize: '1.1rem' }}>Gerrymandering is eliminated</li>
                <li style={{ fontSize: '1.1rem' }}>Presidents are elected by the people, not the states</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Are Section */}
      <section style={{
        padding: '60px 0',
        backgroundColor: colorPalette.light
      }}>
        <div className="container">
          <h2 style={{
            fontSize: '2.2rem',
            color: colorPalette.dark,
            marginBottom: '50px',
            textAlign: 'center'
          }}>Who We Are</h2>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: window.innerWidth < 768 ? '1fr' : 
                              window.innerWidth < 992 ? 'repeat(2, 1fr)' : 
                              'repeat(3, 1fr)',
            gap: '30px',
            marginBottom: '40px'
          }}>
            {/* Card 1 */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '30px',
              boxShadow: '0 4px 10px rgba(0,0,0,0.05)',
              textAlign: 'center'
            }}>
              <div style={{
                backgroundColor: colorPalette.primary,
                color: 'white',
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px'
              }}>
                <Users size={28} />
              </div>
              <h3 style={{ fontSize: '1.4rem', marginBottom: '15px', color: colorPalette.dark }}>
                Diverse Coalitions
              </h3>
              <p style={{ fontSize: '1rem', lineHeight: '1.6' }}>
                We are Americans from diverse political backgrounds, united by the vision of a more democratic and representative political system.
              </p>
            </div>
            
            {/* Card 2 */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '30px',
              boxShadow: '0 4px 10px rgba(0,0,0,0.05)',
              textAlign: 'center'
            }}>
              <div style={{
                backgroundColor: colorPalette.secondary,
                color: 'white',
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px'
              }}>
                <Target size={28} />
              </div>
              <h3 style={{ fontSize: '1.4rem', marginBottom: '15px', color: colorPalette.dark }}>
                Strategic Organizers
              </h3>
              <p style={{ fontSize: '1rem', lineHeight: '1.6' }}>
                Our members are skilled organizers who understand that structural electoral reform 
                is a necessary first step toward meaningful change in American politics.
              </p>
            </div>
            
            {/* Card 3 */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '30px',
              boxShadow: '0 4px 10px rgba(0,0,0,0.05)',
              textAlign: 'center'
            }}>
              <div style={{
                backgroundColor: colorPalette.dark,
                color: 'white',
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px'
              }}>
                <Flag size={28} />
              </div>
              <h3 style={{ fontSize: '1.4rem', marginBottom: '15px', color: colorPalette.dark }}>
                Everyday Americans
              </h3>
              <p style={{ fontSize: '1rem', lineHeight: '1.6' }}>
                Americans are tired of the two-party system. Voters Deserve More offers actionable steps for voters to use their 
                voice and promote improvements to our broken political system that, regardless of political affiliation, we can all agree on.
              </p>
            </div>
          </div>
          
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '30px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
            marginBottom: '30px'
          }}>
            <h3 style={{ 
              fontSize: '1.6rem', 
              color: colorPalette.primary,
              marginBottom: '20px',
              textAlign: 'center'
            }}>Our Values</h3>
            
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: window.innerWidth < 768 ? '1fr' : 'repeat(2, 1fr)',
              gap: '20px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                marginBottom: '20px'
              }}>
                <div style={{
                  backgroundColor: `${colorPalette.primary}20`,
                  color: colorPalette.primary,
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '15px',
                  flexShrink: 0
                }}>
                  <Award size={20} />
                </div>
                <div>
                  <h4 style={{ fontSize: '1.2rem', marginBottom: '8px', color: colorPalette.dark }}>
                    Real Democracy
                  </h4>
                  <p style={{ fontSize: '1rem', lineHeight: '1.6' }}>
                  We recognize the basic truth that democracy means more than two parties loyal only to themselves and their donors. Only a system that 
                  represents all Americans, from all walks of life and viewpoints, is worthy of the word.
                  </p>
                </div>
              </div>
              
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                marginBottom: '20px'
              }}>
                <div style={{
                  backgroundColor: `${colorPalette.primary}20`,
                  color: colorPalette.primary,
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '15px',
                  flexShrink: 0
                }}>
                  <Briefcase size={20} />
                </div>
                <div>
                  <h4 style={{ fontSize: '1.2rem', marginBottom: '8px', color: colorPalette.dark }}>
                    Standing Together
                  </h4>
                  <p style={{ fontSize: '1rem', lineHeight: '1.6' }}>
                    Our cooperation is our greatest strength. No single party, politician, organization, or activist can fix our broken system. But together?
                    We can shake the foundations of our country, from the bottom up. Common people uniting behind a common cause, fighting for the common 
                    good–this is how we win.
                  </p>
                </div>
              </div>
              
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                marginBottom: '20px'
              }}>
                <div style={{
                  backgroundColor: `${colorPalette.primary}20`,
                  color: colorPalette.primary,
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '15px',
                  flexShrink: 0
                }}>
                  <Users size={20} />
                </div>
                <div>
                  <h4 style={{ fontSize: '1.2rem', marginBottom: '8px', color: colorPalette.dark }}>
                    Equality
                  </h4>
                  <p style={{ fontSize: '1rem', lineHeight: '1.6' }}>
                    When all votes are not equal and elections are sold to the highest bidder, there is no democracy. Every American deserves a fair vote, and 
                    every party a fair campaign. 
                  </p>
                </div>
              </div>
              
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                marginBottom: '20px'
              }}>
                <div style={{
                  backgroundColor: `${colorPalette.primary}20`,
                  color: colorPalette.primary,
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '15px',
                  flexShrink: 0
                }}>
                  <Target size={20} />
                </div>
                <div>
                  <h4 style={{ fontSize: '1.2rem', marginBottom: '8px', color: colorPalette.dark }}>
                    Accountability
                  </h4>
                  <p style={{ fontSize: '1rem', lineHeight: '1.6' }}>
                  Politicians belong to the people, not the other way around. We demand representatives directly elected by and beholden to everyday Americans, 
                  not corrupt party institutions, corporate donors, and foreign governments.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section style={{
        padding: '60px 0',
        backgroundColor: colorPalette.primary,
        color: 'white',
        textAlign: 'center'
      }}>
        <div className="container">
          <h2 style={{
            fontSize: '2.5rem',
            marginBottom: '20px'
          }}>Join the Movement</h2>
          <p style={{
            fontSize: '1.25rem',
            maxWidth: '800px',
            margin: '0 auto 30px',
            lineHeight: '1.6'
          }}>
            Ready to transform American democracy? Register with Voters Deserve More to learn how you can help win a democracy for all Americans.
          </p>
          <div style={{
            display: 'flex',
            gap: '20px',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <Link to="/get-involved" style={{
              backgroundColor: 'white',
              color: colorPalette.primary,
              padding: '15px 30px',
              borderRadius: '6px',
              fontWeight: '600',
              textDecoration: 'none',
              fontSize: '1.1rem'
            }}>
              Get Involved
            </Link>
            <Link to="/contact" style={{
              backgroundColor: 'transparent',
              color: 'white',
              padding: '15px 30px',
              borderRadius: '6px',
              fontWeight: '600',
              textDecoration: 'none',
              fontSize: '1.1rem',
              border: '2px solid white'
            }}>
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;